import { Suspense, lazy } from "react";
import { Navigate, Outlet, createBrowserRouter, useLocation, useParams } from "react-router-dom";
import { AccountLayout } from "./components/layout/account-layout.js";
import { Shell } from "./components/layout/shell.js";
import { detectBrowserLocale, readStoredLocale } from "./hooks/use-language.js";
import { AppIntlProvider } from "./i18n/intl-provider.js";
import { DEFAULT_LOCALE, isSupportedLocale } from "./i18n/messages.js";

// Lazy-loaded page components for browser code-splitting.
const HomePage = lazy(() => import("./pages/home.js"));
const NotFoundPage = lazy(() => import("./pages/not-found.js"));
const ErrorPage = lazy(() => import("./pages/error.js"));
const SignInPage = lazy(() => import("./pages/sign-in.js"));
const AccountPage = lazy(() => import("./pages/account.js"));
const AdminPage = lazy(() => import("./pages/admin.js"));
const DocsPage = lazy(() => import("./pages/docs.js"));
const FeaturesPage = lazy(() => import("./pages/features.js"));
const PricingPage = lazy(() => import("./pages/pricing.js"));
const AccountApiKeysPage = lazy(() => import("./pages/account-api-keys.js"));
const AccountUsagePage = lazy(() => import("./pages/account-usage.js"));
const AccountRedactionPage = lazy(() => import("./pages/account-redaction.js"));
const CancelPage = lazy(() => import("./pages/cancel.js"));
const ImprintPage = lazy(() => import("./pages/legal/imprint.js"));
const PrivacyPage = lazy(() => import("./pages/legal/privacy.js"));
const TermsPage = lazy(() => import("./pages/legal/terms.js"));
const AvvPage = lazy(() => import("./pages/legal/avv.js"));

/**
 * Guard component: ensures the :lang param is a supported locale, otherwise
 * redirects to the default locale variant. Wraps everything in AppIntlProvider
 * so all descendant components have access to translations.
 */
function LangGuard() {
  const { lang } = useParams<{ lang?: string }>();
  if (!lang || !isSupportedLocale(lang)) {
    return <Navigate to={`/${DEFAULT_LOCALE}`} replace />;
  }
  return (
    <AppIntlProvider locale={lang}>
      <Shell>
        <Suspense fallback={<div aria-busy="true" />}>
          <Outlet />
        </Suspense>
      </Shell>
    </AppIntlProvider>
  );
}

/**
 * Root redirect: picks a locale from stored pref / browser / default and
 * sends user to /<locale>/.
 *
 * Carries the current location's search string along - `Navigate`'s `to`
 * does not inherit it automatically, and a bare `/${detected}` would
 * silently drop any query string on plain "/". That query string is
 * load-bearing for two entry points: the PWA manifest's
 * start_url ("/?tab=text") and the share-target service worker's redirect
 * ("/?tab=text&shared=1") - use-shared-text.ts reads `shared=1` off the URL
 * once HomePage mounts, which never happens if this redirect strips it
 * first.
 */
export function RootRedirect() {
  const stored = readStoredLocale();
  const detected = stored ?? detectBrowserLocale(navigator.languages ?? [navigator.language]);
  const location = useLocation();
  return <Navigate to={{ pathname: `/${detected}`, search: location.search }} replace />;
}

/**
 * Top-level wrapper for the /admin route. Outside the /:lang guard because
 * the admin portal is English-only (product decision). Reuses Shell so the
 * header/footer match, and provides an English IntlProvider so any
 * descendant that calls useIntl (e.g. the shared Header) resolves.
 */
function AdminShell() {
  return (
    <AppIntlProvider locale="en">
      <Shell>
        <Suspense fallback={<div aria-busy="true" />}>
          <Outlet />
        </Suspense>
      </Shell>
    </AppIntlProvider>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootRedirect />,
  },
  {
    path: "/admin",
    element: <AdminShell />,
    // Pathless layout route so the errorElement renders INSIDE AdminShell,
    // keeping its IntlProvider + Shell (header/footer) in the ancestry. An
    // errorElement on the AdminShell route itself would replace AdminShell and
    // strip the provider, making ErrorPage's useIntl() throw again.
    children: [
      {
        errorElement: <ErrorPage />,
        children: [{ index: true, element: <AdminPage /> }],
      },
    ],
  },
  {
    path: "/:lang",
    element: <LangGuard />,
    // Pathless layout route: keeps AppIntlProvider + Shell + Suspense (from
    // LangGuard) mounted around ErrorPage when a child route throws. See the
    // /admin note above.
    children: [
      {
        errorElement: <ErrorPage />,
        children: [
          { index: true, element: <HomePage /> },
          { path: "auth/sign-in", element: <SignInPage /> },
          { path: "docs", element: <DocsPage /> },
          { path: "features", element: <FeaturesPage /> },
          { path: "pricing", element: <PricingPage /> },
          {
            path: "account",
            element: <AccountLayout />,
            children: [
              { index: true, element: <AccountPage /> },
              { path: "redaction", element: <AccountRedactionPage /> },
              { path: "api-keys", element: <AccountApiKeysPage /> },
              { path: "usage", element: <AccountUsagePage /> },
            ],
          },
          { path: "cancel", element: <CancelPage /> },
          { path: "legal/imprint", element: <ImprintPage /> },
          { path: "legal/privacy", element: <PrivacyPage /> },
          { path: "legal/terms", element: <TermsPage /> },
          { path: "legal/avv", element: <AvvPage /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
